import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useModalBackButton from '../../hooks/useModalBackButton';
import useToast from '../../hooks/useToast';
import DepartmentActivityModal from '../../components/DepartmentActivityModal';
import FloatingField from '../../components/FloatingField';
import ModalShell from '../../components/ModalShell';
import { ModalListSkeleton } from '../../components/Skeleton';
import {
  Building,
  Plus,
  Edit3,
  Save,
  X,
  AlertTriangle,
  Trash2,
  WashingMachine,
  Sofa,
  History,
  Info,
  MapPin,
  Trash
} from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';
import { useDryCleaningEnabled } from '../../utils/featureFlags';
import { API_URL } from '../../config';
import { formatPriceCoefficientLabel } from '../../utils/priceCoefficient';
import { getStageConfig } from '../../utils/stages';
import { haptic } from '../../utils/vibration';

const ORDER_STAGE_LABELS = {
  pickup: 'Доставка от клиента',
  washing: 'Стирка',
  washing_laundry: 'Прачечная',
  washing_cleaning: 'Чистка',
  cleaning: 'Химчистка мебели',
  drying_packing: 'Сушка и упаковка',
  approval: 'Согласование',
  delivery: 'Доставка клиенту',
  handover: 'Выдача',
  pending: 'Ожидает',
  in_progress: 'В работе',
  completed: 'Завершен',
  cancelled: 'Отменен',
};

const formatPriceCoefficientInput = (value) => String(value ?? 1).replace(',', '.');

const normalizePriceCoefficientInput = (value) => (
  String(value ?? '')
    .replace(/\s+/g, '')
    .replace(',', '.')
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*)\./g, '$1')
);

const parsePriceCoefficientValue = (value) => {
  const normalized = normalizePriceCoefficientInput(value);
  if (!normalized || normalized === '.') {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampPriceCoefficient = (value) => Math.min(10, Math.max(0.1, value));

const roundPriceCoefficientStep = (value) => Math.round(value * 10) / 10;

const DepartmentsManagement = () => {
  const isDryCleaningEnabled = useDryCleaningEnabled();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showCoeffInfo, setShowCoeffInfo] = useState(false);
  const [showAddressRulesInfo, setShowAddressRulesInfo] = useState(false);
  const [showCoeffConfirm, setShowCoeffConfirm] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [deleteBlockOrders, setDeleteBlockOrders] = useState([]); // заказы блокирующие удаление
  const [checkingDelete, setCheckingDelete] = useState(false);

  useModalBackButton(showModal, () => {
    haptic('close');
    setShowModal(false);
  });
  useModalBackButton(showHistoryModal, () => {
    haptic('close');
    setShowHistoryModal(false);
    setSelectedDepartment(null);
  });
  useModalBackButton(!!deleteConfirmId, () => {
    haptic('close');
    setDeleteConfirmId(null);
    setDeleteBlockOrders([]);
  });
  useModalBackButton(showCoeffInfo, () => {
    haptic('close');
    setShowCoeffInfo(false);
  });
  useModalBackButton(showAddressRulesInfo, () => {
    haptic('close');
    setShowAddressRulesInfo(false);
  });
  useModalBackButton(showCoeffConfirm, () => {
    haptic('close');
    setShowCoeffConfirm(false);
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    order_types: [],
    client_type: 'all',  // "b2c", "b2b", "all"
    price_coefficient: 1.0,
    price_coefficient_b2c: 1.0,
    price_coefficient_b2b: 1.0,
    address_rules: []  // правила для автоопределения по адресу
  });
  const [priceCoefficientB2CInput, setPriceCoefficientB2CInput] = useState('1.0');
  const [priceCoefficientB2BInput, setPriceCoefficientB2BInput] = useState('1.0');

  // Состояние для подсказок адресов DaData
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const addressDropdownRef = useRef(null);
  const addressInputRef = useRef(null);

  // Загрузка подразделений
  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = await safeFetch(`${API_URL}/api/departments/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setDepartments(data);
    } catch (err) {
      console.error('Ошибка загрузки подразделений:', err);
      showToast(err.message || 'Ошибка загрузки подразделений', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Подсказки адресов через DaData
  const fetchAddressSuggestions = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/dadata/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ query, count: 5 })
      });
      const data = await response.json();
      setAddressSuggestions(Array.isArray(data) ? data : (data.suggestions || []));
      setShowAddressSuggestions(true);
    } catch (err) {
      console.error('Ошибка получения подсказок адресов:', err);
    }
  };

  const selectAddressRule = (suggestion) => {
    const newData = suggestion.data || {};
    const rule = {
      region: newData.region || '',
      city: newData.city || '',
      // Можно добавить больше полей при необходимости
    };

    // Проверяем дубликат
    const exists = formData.address_rules.some(r =>
      r.region === rule.region && r.city === rule.city
    );

    if (!exists) {
      handleInputChange('address_rules', [...formData.address_rules, rule]);
    }

    setAddressQuery('');
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
  };

  const removeAddressRule = (index) => {
    const newRules = [...formData.address_rules];
    newRules.splice(index, 1);
    handleInputChange('address_rules', newRules);
  };

  const openCreateModal = () => {
    haptic('open');
    setModalMode('create');
    setFormData({ name: '', description: '', is_active: true, order_types: [], client_type: 'all', price_coefficient: 1.0, price_coefficient_b2c: 1.0, price_coefficient_b2b: 1.0, address_rules: [] });
    setPriceCoefficientB2CInput('1.0');
    setPriceCoefficientB2BInput('1.0');
    setAddressQuery('');
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setShowModal(true);
  };

  const openEditModal = (department) => {
    haptic('open');
    setModalMode('edit');
    setFormData({
      name: department.name,
      description: department.description || '',
      is_active: department.is_active,
      order_types: Array.isArray(department.order_types) ? department.order_types : [],
      client_type: department.client_type || 'all',
      price_coefficient: department.price_coefficient ?? 1.0,
      price_coefficient_b2c: department.price_coefficient_b2c ?? department.price_coefficient ?? 1.0,
      price_coefficient_b2b: department.price_coefficient_b2b ?? department.price_coefficient ?? 1.0,
      address_rules: Array.isArray(department.address_rules) ? department.address_rules : []
    });
    setPriceCoefficientB2CInput(formatPriceCoefficientInput(department.price_coefficient_b2c ?? department.price_coefficient ?? 1.0));
    setPriceCoefficientB2BInput(formatPriceCoefficientInput(department.price_coefficient_b2b ?? department.price_coefficient ?? 1.0));
    setAddressQuery('');
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setEditingId(department.id);
    setShowModal(true);
  };

  // Открытие модального окна истории
  const openHistoryModal = (department) => {
    haptic('open');
    setSelectedDepartment(department);
    setShowHistoryModal(true);
  };

  const closeModal = () => {
    haptic('close');
    setShowModal(false);
    setFormData({ name: '', description: '', is_active: true, order_types: [], client_type: 'all', price_coefficient: 1.0, price_coefficient_b2c: 1.0, price_coefficient_b2b: 1.0, address_rules: [] });
    setPriceCoefficientB2CInput('1.0');
    setPriceCoefficientB2BInput('1.0');
    setAddressQuery('');
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setShowCoeffConfirm(false);
    setEditingId(null);
  };

  const closeHistoryModal = () => {
    haptic('close');
    setShowHistoryModal(false);
    setSelectedDepartment(null);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      showToast('Укажите название подразделения', 'error');
      return false;
    }

    // Проверка дублирования имени (исключаем мягко удалённые)
    const duplicate = departments.find(d =>
      d.name.trim().toLowerCase() === formData.name.trim().toLowerCase() &&
      d.id !== editingId
    );
    if (duplicate) {
      showToast(`Подразделение с названием "${formData.name.trim()}" уже существует`, 'error');
      return false;
    }

    const hasB2C = formData.client_type === 'b2c' || formData.client_type === 'all';
    const hasB2B = formData.client_type === 'b2b' || formData.client_type === 'all';

    if (hasB2C) {
      const coeffB2C = parsePriceCoefficientValue(priceCoefficientB2CInput);
      if (coeffB2C === null) {
        showToast('Укажите корректный коэффициент для физ. лиц', 'error');
        return false;
      }
      if (coeffB2C < 0.1 || coeffB2C > 10) {
        showToast('Коэффициент для физ. лиц должен быть в диапазоне от 0.1 до 10', 'error');
        return false;
      }
    }

    if (hasB2B) {
      const coeffB2B = parsePriceCoefficientValue(priceCoefficientB2BInput);
      if (coeffB2B === null) {
        showToast('Укажите корректный коэффициент для юр. лиц', 'error');
        return false;
      }
      if (coeffB2B < 0.1 || coeffB2B > 10) {
        showToast('Коэффициент для юр. лиц должен быть в диапазоне от 0.1 до 10', 'error');
        return false;
      }
    }

    return true;
  };

  const executeSave = async () => {
    setSaving(true);

    try {
      const parsedB2C = parsePriceCoefficientValue(priceCoefficientB2CInput) ?? 1.0;
      const parsedB2B = parsePriceCoefficientValue(priceCoefficientB2BInput) ?? 1.0;
      const payload = {
        ...formData,
        price_coefficient: parsedB2C,
        price_coefficient_b2c: parsedB2C,
        price_coefficient_b2b: parsedB2B
      };
      const token = localStorage.getItem('token');
      
      if (modalMode === 'create') {
        await safeFetch(`${API_URL}/api/departments/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }, 20000);
      } else {
        await safeFetch(`${API_URL}/api/departments/${editingId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }, 20000);
      }

      closeModal();
      await fetchDepartments();
      haptic('success');
      showToast(modalMode === 'create' ? 'Подразделение успешно создано' : 'Подразделение успешно сохранено');
    } catch (err) {
      console.error('Ошибка сохранения подразделения:', err);
      haptic('error');
      showToast(err.message || `Ошибка ${modalMode === 'create' ? 'создания' : 'обновления'} подразделения`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const parsedPriceCoefficientB2C = parsePriceCoefficientValue(priceCoefficientB2CInput) ?? 1.0;
  const parsedPriceCoefficientB2B = parsePriceCoefficientValue(priceCoefficientB2BInput) ?? 1.0;

  const handleSave = async () => {
    if (!validateForm()) return;

    if (modalMode === 'edit') {
      const currentDepartment = departments.find((department) => department.id === editingId);
      const oldCoeffB2C = Number(currentDepartment?.price_coefficient_b2c ?? currentDepartment?.price_coefficient ?? 1.0);
      const newCoeffB2C = parsedPriceCoefficientB2C;
      const oldCoeffB2B = Number(currentDepartment?.price_coefficient_b2b ?? currentDepartment?.price_coefficient ?? 1.0);
      const newCoeffB2B = parsedPriceCoefficientB2B;

      const hasB2C = formData.client_type === 'b2c' || formData.client_type === 'all';
      const hasB2B = formData.client_type === 'b2b' || formData.client_type === 'all';

      const b2cChanged = hasB2C && Math.abs(oldCoeffB2C - newCoeffB2C) > 0.0001;
      const b2bChanged = hasB2B && Math.abs(oldCoeffB2B - newCoeffB2B) > 0.0001;

      if (currentDepartment && (b2cChanged || b2bChanged)) {
        haptic('warning');
        setShowCoeffConfirm(true);
        return;
      }
    }

    await executeSave();
  };

  const adjustPriceCoefficientB2C = (delta) => {
    const nextValue = clampPriceCoefficient(roundPriceCoefficientStep(parsedPriceCoefficientB2C + delta));
    setPriceCoefficientB2CInput(nextValue.toFixed(1));
  };

  const adjustPriceCoefficientB2B = (delta) => {
    const nextValue = clampPriceCoefficient(roundPriceCoefficientStep(parsedPriceCoefficientB2B + delta));
    setPriceCoefficientB2BInput(nextValue.toFixed(1));
  };

  const handleDeleteClick = async (id) => {
    haptic('warning');
    setCheckingDelete(true);
    setDeleteBlockOrders([]);
    setDeleteConfirmId(id);
    try {
      const token = localStorage.getItem('token');
      const data = await safeFetch(
        `${API_URL}/api/orders?department_id=${id}&active_only=true&limit=200`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setDeleteBlockOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setDeleteConfirmId(null);
      haptic('error');
      showToast('Не удалось проверить активные заказы: ' + (err.message || 'Ошибка сервера'), 'error');
    } finally {
      setCheckingDelete(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      await safeFetch(`${API_URL}/api/departments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      }, 20000);

      haptic('success');
      showToast('Подразделение успешно удалено');
      setDeleteConfirmId(null);
      setDeleteBlockOrders([]);
      await fetchDepartments();
    } catch (err) {
      console.error('Ошибка удаления подразделения:', err);
      haptic('error');
      showToast(err.message || 'Ошибка удаления подразделения', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Функция для получения названия типа заказа
  const getOrderTypeName = (orderType) => {
    switch (orderType) {
      case 'washing':
        return 'Стирка';
      case 'dry_cleaning':
        return 'Химчистка';
      default:
        return 'Не указан';
    }
  };

  // Функция для получения иконки типа заказа
  const getOrderTypeIcon = (orderType) => {
    switch (orderType) {
      case 'washing':
        return <WashingMachine className="w-4 h-4" />;
      case 'dry_cleaning':
        // Используем иконку дивана для химчистки
        return <Sofa className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getOrderStageLabel = (order) => {
    if (order?.status_label) return order.status_label;
    if (order?.lifecycle_label) return order.lifecycle_label;
    return ORDER_STAGE_LABELS[order?.status] || order?.status || '';
  };

  const renderOrderStage = (order) => {
    const stageName = order?.status;
    const stageConfig = stageName ? getStageConfig(stageName) : null;
    const StageIcon = stageConfig?.icon;
    const isWashingFlowStage = stageName === 'washing_laundry' || stageName === 'washing_cleaning';

    return (
      <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs">
        {StageIcon ? (
          <StageIcon className={isWashingFlowStage ? 'h-3 w-3 shrink-0' : 'h-3.5 w-3.5 shrink-0'} />
        ) : null}
        <span>{getOrderStageLabel(order)}</span>
      </span>
    );
  };

  // Компонент анимации загрузки
  const LoadingSkeleton = () => (
    <div className="space-y-3 animate-pulse">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg"></div>
                <div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-1"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-16"></div>
              </div>
            </div>
            <div className="flex space-x-1 ml-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4 desktop-content-container-wide">
            {/* Заголовок и кнопка создания в стиле UsersList */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Building className="w-5 h-5 text-red-600" />
          <span className="text-base font-medium text-gray-900 dark:text-gray-100">
            Подразделения ({departments.length})
          </span>
        </div>
        
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm font-medium shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Новое</span>
        </button>
      </div>

      {/* Список подразделений или анимация загрузки */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4">
          {loading ? (
            <LoadingSkeleton />
          ) : departments.length === 0 ? (
            <div className="text-center py-12">
              <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Нет подразделений
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Создайте первое подразделение
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {departments.map((department) => (
                <div
                  key={department.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {/* Делаем иконку объемной с градиентом и тенями */}
                        <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-lg">
                          {department.name?.[0]?.toUpperCase() || 'D'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                            {department.name}
                          </h3>
                          {department.description && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {department.description}
                            </p>
                          )}
                          {/* Отображение типов заказов */}
                          {Array.isArray(department.order_types) && department.order_types.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {department.order_types.map((type) => (
                                <span key={type} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                  {getOrderTypeIcon(type)}
                                  <span className="ml-1">{getOrderTypeName(type)}</span>
                                </span>
                              ))}
                            </div>
                          )}
                          {/* Отображение типа клиентов */}
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              department.client_type === 'all' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                              department.client_type === 'b2c' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' :
                              'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
                            }`}>
                              {department.client_type === 'all' ? 'Все клиенты' :
                               department.client_type === 'b2c' ? 'Физ. лица' : 'Юр. лица'}
                            </span>
                          </div>
                          {/* Отображение правил адресов */}
                          {Array.isArray(department.address_rules) && department.address_rules.length > 0 && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                              <MapPin className="w-3 h-3" />
                              <span>{department.address_rules.length} правил адресов</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          department.is_active 
                            ? 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-300' 
                            : 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {department.is_active ? 'Активно' : 'Неактивно'}
                        </div>
                        {department.client_type === 'b2b' ? (
                          <div
                            className="inline-block rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700 dark:bg-purple-900/20 dark:text-purple-300"
                            title="Коэффициент цены для юр. лиц"
                          >
                            Юр: {formatPriceCoefficientLabel(department.price_coefficient_b2b ?? department.price_coefficient ?? 1)}
                          </div>
                        ) : department.client_type === 'b2c' ? (
                          <div
                            className="inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
                            title="Коэффициент цены для физ. лиц"
                          >
                            Физ: {formatPriceCoefficientLabel(department.price_coefficient_b2c ?? department.price_coefficient ?? 1)}
                          </div>
                        ) : (
                          <>
                            <div
                              className="inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
                              title="Коэффициент цены для физ. лиц"
                            >
                              Физ: {formatPriceCoefficientLabel(department.price_coefficient_b2c ?? department.price_coefficient ?? 1)}
                            </div>
                            <div
                              className="inline-block rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700 dark:bg-purple-900/20 dark:text-purple-300"
                              title="Коэффициент цены для юр. лиц"
                            >
                              Юр: {formatPriceCoefficientLabel(department.price_coefficient_b2b ?? department.price_coefficient ?? 1)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex space-x-1 ml-3">
                      {/* Кнопка просмотра истории */}
                      <button
                        onClick={() => openHistoryModal(department)}
                        className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="История изменений"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(department)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Редактировать"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(department.id)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Удалить"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно для создания/редактирования подразделения */}
      <ModalShell
        open={showModal}
        onClose={closeModal}
        variant="window"
        panelClassName="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {modalMode === 'create' ? 'Создание подразделения' : 'Редактирование подразделения'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex flex-col gap-4">
                <div className="order-1">
                  <FloatingField label="Название подразделения *" error={!formData.name.trim()}>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                      placeholder="Введите название подразделения"
                    />
                  </FloatingField>
                  {/* Отображаем сообщение об ошибке под полем ввода */}
                  {!formData.name.trim() && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Укажите название подразделения
                    </p>
                  )}
                </div>
                
                <div className="order-2">
                  <FloatingField label="Описание">
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full min-h-[96px] max-h-40 resize-none overflow-y-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                      placeholder="Введите описание подразделения"
                      rows="4"
                      maxLength={280}
                    />
                  </FloatingField>
                  <div className="mt-1 text-right text-[11px] text-gray-400 dark:text-gray-500">
                    {formData.description.length}/280
                  </div>
                </div>
                
                {/* Выбор типов заказов */}
                <div className="order-5">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Типы заказов
                  </label>
                  <div className="space-y-3">
                    {/* Стирка */}
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        const hasWashing = formData.order_types.includes('washing');
                        const hasDryCleaning = formData.order_types.includes('dry_cleaning');
                        
                        if (hasWashing && hasDryCleaning) {
                          // Были оба, выключаем стирку -> остаётся химчистка
                          handleInputChange('order_types', ['dry_cleaning']);
                        } else if (hasWashing) {
                          // Была только стирка, переключаем на химчистку
                          handleInputChange('order_types', ['dry_cleaning']);
                        } else {
                          // Не было стирки, включаем
                          handleInputChange('order_types', [...formData.order_types, 'washing']);
                        }
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                        formData.order_types.includes('washing')
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.order_types.includes('washing')
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                        }`}>
                          <WashingMachine className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Стирка</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Прачечные услуги</div>
                        </div>
                      </div>
                      <div className={`w-12 h-7 rounded-full relative transition-colors ${
                        formData.order_types.includes('washing')
                          ? 'bg-blue-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
                          formData.order_types.includes('washing')
                            ? 'translate-x-5'
                            : 'translate-x-0.5'
                        }`} />
                      </div>
                    </button>

                    {/* Химчистка */}
                    {isDryCleaningEnabled && (
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        const hasWashing = formData.order_types.includes('washing');
                        const hasDryCleaning = formData.order_types.includes('dry_cleaning');
                        
                        if (hasWashing && hasDryCleaning) {
                          // Были оба, выключаем химчистку -> остаётся стирка
                          handleInputChange('order_types', ['washing']);
                        } else if (hasDryCleaning) {
                          // Была только химчистка, переключаем на стирку
                          handleInputChange('order_types', ['washing']);
                        } else {
                          // Не было химчистки, включаем
                          handleInputChange('order_types', [...formData.order_types, 'dry_cleaning']);
                        }
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                        formData.order_types.includes('dry_cleaning')
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.order_types.includes('dry_cleaning')
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                        }`}>
                          <Sofa className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Химчистка</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Химчистка мебели</div>
                        </div>
                      </div>
                      <div className={`w-12 h-7 rounded-full relative transition-colors ${
                        formData.order_types.includes('dry_cleaning')
                          ? 'bg-blue-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
                          formData.order_types.includes('dry_cleaning')
                            ? 'translate-x-5'
                            : 'translate-x-0.5'
                        }`} />
                      </div>
                    </button>
                    )}
                  </div>
                </div>

                {/* Выбор типа клиентов */}
                <div className="order-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Тип клиентов
                  </label>
                  <div className="space-y-3">
                    {/* Физ. лица */}
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        const hasB2C = formData.client_type === 'b2c' || formData.client_type === 'all';
                        const hasB2B = formData.client_type === 'b2b' || formData.client_type === 'all';
                        
                        if (hasB2C && hasB2B) {
                          // Были оба, выключаем b2c -> остаётся b2b
                          handleInputChange('client_type', 'b2b');
                        } else if (hasB2C) {
                          // Был только b2c, выключаем -> b2b
                          handleInputChange('client_type', 'b2b');
                        } else {
                          // Не было b2c, включаем
                          if (hasB2B) {
                            handleInputChange('client_type', 'all');
                          } else {
                            handleInputChange('client_type', 'b2c');
                          }
                        }
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                        formData.client_type === 'b2c' || formData.client_type === 'all'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.client_type === 'b2c' || formData.client_type === 'all'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                        }`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Физ. лица</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">B2C клиенты</div>
                        </div>
                      </div>
                      <div className={`w-12 h-7 rounded-full relative transition-colors ${
                        formData.client_type === 'b2c' || formData.client_type === 'all'
                          ? 'bg-blue-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
                          formData.client_type === 'b2c' || formData.client_type === 'all'
                            ? 'translate-x-5'
                            : 'translate-x-0.5'
                        }`} />
                      </div>
                    </button>

                    {/* Юр. лица */}
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        const hasB2C = formData.client_type === 'b2c' || formData.client_type === 'all';
                        const hasB2B = formData.client_type === 'b2b' || formData.client_type === 'all';
                        
                        if (hasB2C && hasB2B) {
                          // Были оба, выключаем b2b -> остаётся b2c
                          handleInputChange('client_type', 'b2c');
                        } else if (hasB2B) {
                          // Был только b2b, выключаем -> b2c
                          handleInputChange('client_type', 'b2c');
                        } else {
                          // Не было b2b, включаем
                          if (hasB2C) {
                            handleInputChange('client_type', 'all');
                          } else {
                            handleInputChange('client_type', 'b2b');
                          }
                        }
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                        formData.client_type === 'b2b' || formData.client_type === 'all'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.client_type === 'b2b' || formData.client_type === 'all'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                        }`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Юр. лица</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">B2B компании</div>
                        </div>
                      </div>
                      <div className={`w-12 h-7 rounded-full relative transition-colors ${
                        formData.client_type === 'b2b' || formData.client_type === 'all'
                          ? 'bg-blue-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
                          formData.client_type === 'b2b' || formData.client_type === 'all'
                            ? 'translate-x-5'
                            : 'translate-x-0.5'
                        }`} />
                      </div>
                    </button>
                  </div>
                </div>
                
                {/* Переключатель активности (выделен фоном) */}
                <div className="order-7 p-3 bg-gray-100 dark:bg-gray-800 rounded-xl">
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        handleInputChange('is_active', !formData.is_active);
                      }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                      formData.is_active
                        ? 'bg-white dark:bg-gray-700 shadow-sm'
                        : 'bg-white/50 dark:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        formData.is_active
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-300 dark:bg-gray-600 text-gray-500'
                      }`}>
                        {formData.is_active ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Активно</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formData.is_active ? 'Подразделение активно' : 'Подразделение неактивно'}
                        </div>
                      </div>
                    </div>
                    <div className={`w-12 h-7 rounded-full relative transition-colors ${
                      formData.is_active
                        ? 'bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}>
                      <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
                        formData.is_active
                          ? 'translate-x-5'
                          : 'translate-x-0.5'
                      }`} />
                    </div>
                  </button>
                </div>
                
                {/* Коэффициенты цены */}
                <div className="order-3 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      Коэффициенты цены
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        haptic('open');
                        setShowCoeffInfo(true);
                      }}
                      className="text-gray-400 hover:text-blue-500 transition-colors"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Для физ. лиц */}
                  {(formData.client_type === 'b2c' || formData.client_type === 'all') && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700/60">
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center justify-between">
                        <span>Для физ. лиц (B2C)</span>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                          {parsedPriceCoefficientB2C === 1
                            ? 'Стандартные цены'
                            : parsedPriceCoefficientB2C > 1
                              ? `+${Math.round((parsedPriceCoefficientB2C - 1) * 100)}% к цене`
                              : `-${Math.round((1 - parsedPriceCoefficientB2C) * 100)}% к цене`}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => adjustPriceCoefficientB2C(-0.1)}
                          disabled={parsedPriceCoefficientB2C <= 0.1}
                          className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-base font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                          aria-label="Уменьшить коэффициент для физ. лиц"
                        >
                          -
                        </button>
                        <input
                          type="text"
                          inputMode="decimal"
                          enterKeyHint="done"
                          value={priceCoefficientB2CInput}
                          onChange={(e) => {
                            const nextValue = normalizePriceCoefficientInput(e.target.value);
                            setPriceCoefficientB2CInput(nextValue);
                          }}
                          placeholder="1.0"
                          className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white text-center dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => adjustPriceCoefficientB2C(0.1)}
                          disabled={parsedPriceCoefficientB2C >= 10}
                          className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-base font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                          aria-label="Увеличить коэффициент для физ. лиц"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Для юр. лиц */}
                  {(formData.client_type === 'b2b' || formData.client_type === 'all') && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700/60">
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center justify-between">
                        <span>Для юр. лиц (B2B)</span>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                          {parsedPriceCoefficientB2B === 1
                            ? 'Стандартные цены'
                            : parsedPriceCoefficientB2B > 1
                              ? `+${Math.round((parsedPriceCoefficientB2B - 1) * 100)}% к цене`
                              : `-${Math.round((1 - parsedPriceCoefficientB2B) * 100)}% к цене`}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => adjustPriceCoefficientB2B(-0.1)}
                          disabled={parsedPriceCoefficientB2B <= 0.1}
                          className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-base font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                          aria-label="Уменьшить коэффициент для юр. лиц"
                        >
                          -
                        </button>
                        <input
                          type="text"
                          inputMode="decimal"
                          enterKeyHint="done"
                          value={priceCoefficientB2BInput}
                          onChange={(e) => {
                            const nextValue = normalizePriceCoefficientInput(e.target.value);
                            setPriceCoefficientB2BInput(nextValue);
                          }}
                          placeholder="1.0"
                          className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white text-center dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => adjustPriceCoefficientB2B(0.1)}
                          disabled={parsedPriceCoefficientB2B >= 10}
                          className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-base font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                          aria-label="Увеличить коэффициент для юр. лиц"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Правила адресов для автоопределения подразделения */}
                <div className="order-4">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      Адреса для автоопределения
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        haptic('open');
                        setShowAddressRulesInfo(true);
                      }}
                      className="text-gray-400 hover:text-blue-500 transition-colors"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Показ добавленных правил */}
                  {formData.address_rules.length > 0 && (
                    <div className="space-y-1 mb-3 max-h-32 overflow-y-auto">
                      {formData.address_rules.map((rule, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300 truncate">
                              {rule.region}{rule.city ? `, ${rule.city}` : ''}{rule.city_part ? `, ${rule.city_part}` : ''}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              haptic('tap');
                              removeAddressRule(index);
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                          >
                            <Trash className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Поле ввода с подсказками DaData */}
                  <div className="relative">
                    <FloatingField label="Новый адрес">
                      <input
                        ref={addressInputRef}
                        type="text"
                        value={addressQuery}
                        onChange={(e) => {
                          setAddressQuery(e.target.value);
                          fetchAddressSuggestions(e.target.value);
                        }}
                        onFocus={() => {
                          if (addressSuggestions.length > 0) setShowAddressSuggestions(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowAddressSuggestions(false), 200);
                        }}
                        className="w-full px-3 py-2 rounded-lg focus:outline-none bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                        placeholder="Начните вводить адрес..."
                      />
                    </FloatingField>

                    {/* Выпадающий список подсказок */}
                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <div
                        ref={addressDropdownRef}
                        className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                      >
                        {addressSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            onMouseDown={() => {
                              haptic('selection');
                              selectAddressRule(suggestion);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-700 last:border-0"
                          >
                            <div className="text-gray-700 dark:text-gray-300 truncate">
                              {suggestion.value}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {formData.address_rules.length === 0
                      ? 'Добавьте адреса — подразделение будет выбираться автоматически'
                      : `Добавлено правил: ${formData.address_rules.length}`}
                  </p>
                </div>

                <div className="order-8 flex gap-2">
                  <button
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={() => {
                      haptic('selection');
                      handleSave();
                    }}
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 text-sm font-medium"
                  >
                    <Save className="w-4 h-4" />
                    <span>{saving ? (modalMode === 'create' ? 'Создание...' : 'Сохранение...') : (modalMode === 'create' ? 'Создать' : 'Сохранить')}</span>
                  </button>
                </div>
              </div>
      </ModalShell>

      <DepartmentActivityModal
        open={showHistoryModal}
        onClose={closeHistoryModal}
        department={selectedDepartment}
      />

      {/* Подтверждение удаления */}
      <ModalShell
        open={Boolean(deleteConfirmId)}
        onClose={() => { haptic('close'); setDeleteConfirmId(null); setDeleteBlockOrders([]); }}
        variant="sheet"
        panelClassName="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900"
      >
        {deleteConfirmId && (
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Удаление подразделения
              </h3>
            </div>

            {checkingDelete ? (
              <div className="py-2">
                <ModalListSkeleton count={3} />
              </div>
            ) : deleteBlockOrders.length > 0 ? (
              <>
                <div className="flex items-start gap-2 mb-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl">
                  <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Нельзя удалить — есть незакрытые заказы ({deleteBlockOrders.length}):
                  </p>
                </div>
                <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 mb-4">
                  {deleteBlockOrders.map((order, i) => (
                    <div key={order.id || i} className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0 text-sm">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        №{order.order_number || order.id}
                      </span>
                      {renderOrderStage(order)}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { haptic('close'); setDeleteConfirmId(null); setDeleteBlockOrders([]); }}
                  className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
                >
                  Понятно
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
                  Вы уверены, что хотите удалить это подразделение? Это действие нельзя отменить.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { haptic('close'); setDeleteConfirmId(null); setDeleteBlockOrders([]); }}
                    className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium text-sm"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={() => {
                      haptic('selection');
                      handleDelete(deleteConfirmId);
                    }}
                    disabled={saving}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors font-medium text-sm"
                  >
                    {saving ? 'Удаление...' : 'Удалить'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </ModalShell>

      {/* Подтверждение изменения коэффициента */}
      <ModalShell
        open={showCoeffConfirm}
        onClose={() => {
          haptic('close');
          setShowCoeffConfirm(false);
        }}
        variant="sheet"
        panelClassName="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Изменить коэффициент?
          </h3>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Новый коэффициент начнёт применяться только к заказам, которые будут созданы после сохранения изменений.
        </p>

        {(() => {
          const currentDepartment = departments.find((department) => department.id === editingId);
          const oldCoeffB2C = Number(currentDepartment?.price_coefficient_b2c ?? currentDepartment?.price_coefficient ?? 1.0);
          const oldCoeffB2B = Number(currentDepartment?.price_coefficient_b2b ?? currentDepartment?.price_coefficient ?? 1.0);
          const hasB2C = formData.client_type === 'b2c' || formData.client_type === 'all';
          const hasB2B = formData.client_type === 'b2b' || formData.client_type === 'all';

          return (
            <div className="space-y-3 mb-5">
              {hasB2C && Math.abs(oldCoeffB2C - parsedPriceCoefficientB2C) > 0.0001 && (
                <div className="rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 px-4 py-3">
                  <div className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2">Физ. лица (B2C)</div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Было</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatPriceCoefficientLabel(oldCoeffB2C)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-500 dark:text-gray-400">Станет</span>
                    <span className="font-semibold text-amber-700 dark:text-amber-300">
                      {formatPriceCoefficientLabel(parsedPriceCoefficientB2C)}
                    </span>
                  </div>
                </div>
              )}
              {hasB2B && Math.abs(oldCoeffB2B - parsedPriceCoefficientB2B) > 0.0001 && (
                <div className="rounded-xl bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 px-4 py-3">
                  <div className="text-xs font-semibold text-purple-800 dark:text-purple-200 mb-2">Юр. лица (B2B)</div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Было</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatPriceCoefficientLabel(oldCoeffB2B)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-500 dark:text-gray-400">Станет</span>
                    <span className="font-semibold text-purple-700 dark:text-purple-300">
                      {formatPriceCoefficientLabel(parsedPriceCoefficientB2B)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        <div className="flex gap-3">
          <button
            onClick={() => {
              haptic('close');
              setShowCoeffConfirm(false);
            }}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium text-sm"
          >
            Отмена
          </button>
          <button
            onClick={async () => {
              haptic('selection');
              setShowCoeffConfirm(false);
              await executeSave();
            }}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:opacity-50 transition-colors font-medium text-sm"
          >
            {saving ? 'Сохранение...' : 'Подтвердить'}
          </button>
        </div>
      </ModalShell>

      {/* Инфо о коэффициенте цены */}
      <ModalShell
        open={showCoeffInfo}
        onClose={() => {
          haptic('close');
          setShowCoeffInfo(false);
        }}
        variant="sheet"
        panelClassName="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-sm shadow-xl"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Коэффициент цены</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Коэффициенты умножаются на стандартные цены всех услуг в заказах этого подразделения. Вы можете задать раздельные коэффициенты для физических лиц (B2C) и юридических лиц / организаций (B2B).
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-4 list-disc list-inside">
          <li>Значение <strong>1.0</strong> — стандартные цены</li>
          <li>Значение <strong>1.5</strong> — цены выше на 50%</li>
          <li>Значение <strong>0.8</strong> — цены ниже на 20%</li>
        </ul>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
          Коэффициент сохраняется в заказе при создании, поэтому его изменение влияет только на новые заказы. Скидки и фиксированные цены услуг коэффициентом не умножаются.
        </p>
        <button
          onClick={() => {
            haptic('close');
            setShowCoeffInfo(false);
          }}
          className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
        >
          Понятно
        </button>
      </ModalShell>

      {/* Инфо о правилах адресов */}
      <ModalShell
        open={showAddressRulesInfo}
        onClose={() => {
          haptic('close');
          setShowAddressRulesInfo(false);
        }}
        variant="sheet"
        panelClassName="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-sm shadow-xl"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Автоопределение по адресу</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          При создании заказа подразделение будет выбрано автоматически, если адрес совпадает с одним из правил.
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-4 list-disc list-inside">
          <li>Совпадение проверяется по <strong>региону</strong> и <strong>городу</strong></li>
          <li>Если правил нет — подразделение показывается всегда</li>
          <li>Если совпало несколько — показываются все подходящие</li>
        </ul>
        <button
          onClick={() => {
            haptic('close');
            setShowAddressRulesInfo(false);
          }}
          className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
        >
          Понятно
        </button>
      </ModalShell>
    </div>
  );
};

export default DepartmentsManagement;
